import React, { useState, useEffect } from 'react';
import { CircleCheck as CheckCircle, Circle as XCircle, Clock, CircleAlert as AlertCircle, ChevronDown, ChevronUp, User, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface FieldChange {
  field: string;
  label: string;
  old_value: string;
  new_value: string;
}

interface EditRequest {
  id: string;
  seller_id: string;
  status: 'pending' | 'approved' | 'rejected';
  changes: FieldChange[];
  rejection_reason: string | null;
  created_at: string;
  reviewed_at: string | null;
  contract_id: string;
  contract: { client_name: string; cpf: string; ruta: number } | null;
  seller: { name: string; email: string } | null;
}

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'all';

export default function ClientEditReview() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<EditRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) fetchRequests();
  }, [user, statusFilter]);

  const fetchRequests = async () => {
    if (!user) return;
    setLoading(true);
    try {
      let query = supabase
        .from('client_edit_requests')
        .select(`
          id, seller_id, status, changes, rejection_reason, created_at, reviewed_at, contract_id,
          contract:contracts(client_name, cpf, ruta),
          seller:users!client_edit_requests_seller_id_fkey(name, email)
        `)
        .eq('unit_id', user.unit_id)
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      setRequests((data || []) as any);
    } catch {
      setMessage({ type: 'error', text: 'Erro ao carregar solicitacoes' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (req: EditRequest) => {
    if (!user) return;
    setProcessingId(req.id);
    setMessage(null);
    try {
      const contractUpdates: Record<string, any> = {};
      const installmentChanges: { id: string; fields: { field: string; new: string }[] }[] = [];

      req.changes.forEach(c => {
        if (c.field === 'installments_data') {
          try {
            const parsed = JSON.parse(c.new_value);
            parsed.forEach((ic: any) => installmentChanges.push(ic));
          } catch { /* skip malformed */ }
        } else if (c.field === 'relative_phones') {
          try { contractUpdates[c.field] = JSON.parse(c.new_value); } catch { contractUpdates[c.field] = c.new_value; }
        } else {
          contractUpdates[c.field] = c.new_value;
        }
      });

      if (Object.keys(contractUpdates).length > 0) {
        const { error } = await supabase.from('contracts').update(contractUpdates).eq('id', req.contract_id);
        if (error) throw error;
      }

      for (const ic of installmentChanges) {
        const instUpdate: Record<string, any> = {};
        ic.fields.forEach((f: any) => { instUpdate[f.field] = f.new; });
        const { error } = await supabase.from('installments').update(instUpdate).eq('id', ic.id);
        if (error) throw error;
      }

      const { error: reqError } = await supabase
        .from('client_edit_requests')
        .update({ status: 'approved', reviewed_by: user.id, reviewed_at: new Date().toISOString() })
        .eq('id', req.id);
      if (reqError) throw reqError;

      await notifySeller(req, 'client_edit_approved', 'Edicao de Cliente Aprovada',
        `Sua solicitacao de edicao para ${req.contract?.client_name} foi aprovada.`);

      setMessage({ type: 'success', text: `Edicao de ${req.contract?.client_name} aprovada e aplicada!` });
      fetchRequests();
    } catch {
      setMessage({ type: 'error', text: 'Erro ao aprovar solicitacao' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (req: EditRequest) => {
    if (!user || !rejectionReason.trim()) return;
    setProcessingId(req.id);
    setMessage(null);
    try {
      const { error } = await supabase
        .from('client_edit_requests')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason.trim(),
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', req.id);
      if (error) throw error;

      await notifySeller(req, 'client_edit_rejected', 'Edicao de Cliente Recusada',
        `Sua solicitacao de edicao para ${req.contract?.client_name} foi recusada. Motivo: ${rejectionReason.trim()}`);

      setMessage({ type: 'success', text: 'Solicitacao recusada com sucesso.' });
      setRejectingId(null);
      setRejectionReason('');
      fetchRequests();
    } catch {
      setMessage({ type: 'error', text: 'Erro ao recusar solicitacao' });
    } finally {
      setProcessingId(null);
    }
  };

  const notifySeller = async (req: EditRequest, type: string, title: string, msgText: string) => {
    if (!req.seller_id) return;
    await supabase.from('notifications').insert({
      user_id: req.seller_id,
      type,
      title,
      message: msgText,
      is_read: false,
      metadata: {
        request_id: req.id,
        client_name: req.contract?.client_name,
        rejection_reason: type === 'client_edit_rejected' ? rejectionReason.trim() : undefined,
        reviewed_by: user?.name,
      },
    });
  };

  const statusBadge = (status: string) => {
    if (status === 'pending') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"><Clock className="w-3 h-3" />Pendente</span>;
    if (status === 'approved') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" />Aprovado</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3" />Recusado</span>;
  };

  if (user?.role !== 'manager') {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-gray-600">Acesso restrito a gestores.</p>
        </div>
      </div>
    );
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Revisao de Edicoes de Clientes</h1>
          <p className="text-sm text-gray-500 mt-1">Aprove ou recuse as solicitacoes de edicao enviadas pelos vendedores.</p>
        </div>
        <button onClick={fetchRequests} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors" title="Atualizar">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {message && (
        <div className={`flex items-start gap-3 p-4 rounded-xl border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
          <p className="text-sm font-medium">{message.text}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {(['pending', 'all', 'approved', 'rejected'] as StatusFilter[]).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${statusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
          >
            {s === 'pending' ? `Pendentes${pendingCount > 0 && statusFilter !== 'pending' ? ` (${pendingCount})` : ''}` : s === 'all' ? 'Todas' : s === 'approved' ? 'Aprovadas' : 'Recusadas'}
          </button>
        ))}
      </div>

      {/* Request list */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3" />
            <p className="text-sm text-gray-500">Carregando solicitacoes...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhuma solicitacao {statusFilter !== 'all' ? `${statusFilter === 'pending' ? 'pendente' : statusFilter === 'approved' ? 'aprovada' : 'recusada'}` : ''}</p>
          </div>
        ) : (
          requests.map(req => (
            <div key={req.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <button
                onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{req.contract?.client_name || '—'}</p>
                    <p className="text-xs text-gray-500">
                      Vendedor: {req.seller?.name || '—'} · {new Date(req.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-400">{req.changes.length} campo{req.changes.length !== 1 ? 's' : ''}</span>
                  {statusBadge(req.status)}
                  {expandedId === req.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {/* Expanded detail */}
              {expandedId === req.id && (
                <div className="border-t border-gray-100 px-6 py-5 space-y-4">
                  {/* Changes table */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Alteracoes Propostas</h3>
                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Campo</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Valor Atual</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Novo Valor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {req.changes.map((c, i) => {
                            if (c.field === 'installments_data') {
                              let parsed: any[] = [];
                              try { parsed = JSON.parse(c.new_value); } catch { parsed = []; }
                              const fieldLabels: Record<string, string> = {
                                due_date: 'Vencimento', amount: 'Valor', status: 'Status',
                                paid_amount: 'Valor Pago', interest: 'Juros'
                              };
                              return (
                                <tr key={i} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium text-gray-700 align-top">{c.label}</td>
                                  <td colSpan={2} className="px-4 py-3">
                                    <div className="space-y-2">
                                      {parsed.map((ic: any, j: number) => (
                                        <div key={j} className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                                          <p className="text-xs font-semibold text-blue-700 mb-1">Parcela {ic.index}</p>
                                          {ic.fields.map((f: any, k: number) => (
                                            <div key={k} className="text-xs text-gray-700 flex gap-2">
                                              <span className="font-medium">{fieldLabels[f.field] || f.field}:</span>
                                              <span className="line-through text-gray-400">{f.old}</span>
                                              <span>→</span>
                                              <span className="font-medium text-blue-700">{f.new}</span>
                                            </div>
                                          ))}
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                            return (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-700">{c.label}</td>
                                <td className="px-4 py-3 text-gray-400 line-through">{c.old_value || '—'}</td>
                                <td className="px-4 py-3 text-gray-900 font-medium">{c.new_value || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Rejection reason if rejected */}
                  {req.status === 'rejected' && req.rejection_reason && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                      <p className="text-xs font-semibold text-red-800 mb-1">Motivo da recusa:</p>
                      <p className="text-sm text-red-700">{req.rejection_reason}</p>
                    </div>
                  )}

                  {/* Actions — only for pending */}
                  {req.status === 'pending' && (
                    <div className="space-y-3">
                      {rejectingId === req.id ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Motivo da recusa <span className="text-red-500">*</span></label>
                            <textarea
                              rows={3}
                              value={rejectionReason}
                              onChange={e => setRejectionReason(e.target.value)}
                              placeholder="Explique o motivo da recusa..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent outline-none resize-none"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleReject(req)}
                              disabled={!rejectionReason.trim() || processingId === req.id}
                              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                              <XCircle className="w-4 h-4" />
                              {processingId === req.id ? 'Recusando...' : 'Confirmar Recusa'}
                            </button>
                            <button
                              onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(req)}
                            disabled={processingId === req.id}
                            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" />
                            {processingId === req.id ? 'Aprovando...' : 'Aprovar'}
                          </button>
                          <button
                            onClick={() => { setRejectingId(req.id); setExpandedId(req.id); }}
                            disabled={processingId === req.id}
                            className="flex items-center gap-2 px-5 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-xl text-sm font-medium hover:bg-red-100 disabled:opacity-50 transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            Recusar
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {req.status !== 'pending' && req.reviewed_at && (
                    <p className="text-xs text-gray-400">
                      Revisado em {new Date(req.reviewed_at).toLocaleString('pt-BR')}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
